import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { KeyRound, ShieldCheck, Calendar, CheckCircle2, ChevronRight, FileText } from 'lucide-react';
import { toast } from 'sonner';

/**
 * First-login wizard for board_director users. Triggered automatically
 * when /board/onboarding/state reports must_change_password OR
 * onboarding_complete === false.
 *
 * 3 steps:
 *   1. Change password    (forced if must_change_password)
 *   2. Sign annual disclosure (skippable, warns first)
 *   3. Preview upcoming meetings + packets (informational)
 */
export default function BoardOnboardingWizard() {
  const { user, api, checkAuth } = useAuth();
  const isDirector = user?.role === 'board_director';

  const [open, setOpen] = useState(false);
  const [state, setState] = useState(null);
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  // Password form
  const [pwForm, setPwForm] = useState({ current: '', next: '', confirm: '' });
  // Disclosure form
  const [discForm, setDiscForm] = useState({ has_conflicts: false, conflicts_text: '' });

  useEffect(() => {
    if (!isDirector) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/board/onboarding/state');
        if (cancelled) return;
        setState(data);
        const incomplete = data.must_change_password || data.onboarding_complete === false;
        if (incomplete) {
          setStep(data.must_change_password ? 1 : (data.disclosure_signed ? 3 : 2));
          setOpen(true);
        }
      } catch {
        // silent — non-blocking
      }
    })();
    return () => { cancelled = true; };
  }, [isDirector, api]);

  if (!isDirector || !state) return null;

  const changePassword = async () => {
    if (!pwForm.current || !pwForm.next) { toast.error('All password fields are required'); return; }
    if (pwForm.next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current, new_password: pwForm.next });
      toast.success('Password updated');
      if (checkAuth) await checkAuth();
      setStep(state.disclosure_signed ? 3 : 2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };

  const signDisclosure = async () => {
    setBusy(true);
    try {
      await api.post('/board/onboarding/sign-disclosure', {
        year: state.year,
        has_conflicts: discForm.has_conflicts,
        conflicts_text: discForm.has_conflicts ? discForm.conflicts_text.trim() : '',
      });
      toast.success(`${state.year} disclosure signed`);
      setState(s => ({ ...s, disclosure_signed: true }));
      setStep(3);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save disclosure');
    } finally { setBusy(false); }
  };

  const skipDisclosure = () => {
    if (!window.confirm('You can sign your disclosure later, but board policy typically requires it on file. Skip for now?')) return;
    setStep(3);
  };

  const finishWizard = async () => {
    setBusy(true);
    try {
      await api.post('/board/onboarding/complete');
      if (checkAuth) await checkAuth();
      setOpen(false);
      toast.success(`Welcome, ${user.name?.split(' ')[0] || 'Director'}!`);
    } catch (e) {
      toast.error('Failed to finish onboarding');
    } finally { setBusy(false); }
  };

  const StepDot = ({ n, label }) => {
    const active = step === n;
    const done = step > n || (n === 1 && !state.must_change_password) || (n === 2 && state.disclosure_signed);
    return (
      <div className="flex items-center gap-2 flex-1">
        <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-all
          ${active ? 'bg-[#6B3FA0] text-white shadow-md' : done ? 'bg-[#43A047] text-white' : 'bg-[#F0EDF6] text-[#8E85A0]'}`}>
          {done ? <CheckCircle2 className="h-4 w-4" /> : n}
        </div>
        <span className={`text-xs ${active ? 'font-bold text-[#2D1B4E]' : 'text-[#8E85A0]'}`}>{label}</span>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && step === 3) setOpen(false); }}>
      <DialogContent
        className="max-w-2xl"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => state.must_change_password && e.preventDefault()}
        data-testid="board-onboarding-wizard">
        <DialogHeader>
          <DialogTitle className="text-[#2D1B4E] flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[#6B3FA0]" /> Welcome to the Board Portal
          </DialogTitle>
          <p className="text-xs text-[#5A4D6E] mt-1">
            Three quick steps so you're set up to govern. This only takes a minute.
          </p>
        </DialogHeader>

        {/* Step progress */}
        <div className="flex items-center gap-3 px-1 py-3 border-b border-[#F0EDF6]">
          <StepDot n={1} label="Set Password" />
          <ChevronRight className="h-3 w-3 text-[#DED8EB]" />
          <StepDot n={2} label="Sign Disclosure" />
          <ChevronRight className="h-3 w-3 text-[#DED8EB]" />
          <StepDot n={3} label="Review Meetings" />
        </div>

        {/* === Step 1: Password === */}
        {step === 1 && (
          <div className="space-y-3 pt-3" data-testid="onboarding-step-password">
            <div className="flex items-center gap-2 text-[#2D1B4E]">
              <KeyRound className="h-4 w-4 text-[#6B3FA0]" />
              <h3 className="font-bold">Set your password</h3>
            </div>
            <p className="text-xs text-[#5A4D6E]">
              You're using a temporary password from the admin. Choose your own (8+ characters) to continue.
            </p>
            <div>
              <Label className="text-xs">Current (temporary) password *</Label>
              <Input type="password" value={pwForm.current} onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))} data-testid="ob-current-pw" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">New password *</Label>
                <Input type="password" value={pwForm.next} onChange={e => setPwForm(f => ({ ...f, next: e.target.value }))} data-testid="ob-new-pw" />
              </div>
              <div>
                <Label className="text-xs">Confirm new password *</Label>
                <Input type="password" value={pwForm.confirm} onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))} data-testid="ob-confirm-pw" />
              </div>
            </div>
            <div className="flex justify-end pt-3">
              <Button onClick={changePassword} disabled={busy} className="bg-[#6B3FA0] hover:bg-[#5A2F8F]" data-testid="ob-change-pw-btn">
                {busy ? 'Saving…' : 'Save & continue'} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* === Step 2: Disclosure === */}
        {step === 2 && (
          <div className="space-y-3 pt-3" data-testid="onboarding-step-disclosure">
            <div className="flex items-center gap-2 text-[#2D1B4E]">
              <ShieldCheck className="h-4 w-4 text-[#6B3FA0]" />
              <h3 className="font-bold">Annual Conflict-of-Interest Disclosure — {state.year}</h3>
            </div>
            <p className="text-xs text-[#5A4D6E]">
              State whether you have any financial or familial relationships that could create a conflict of interest with The Next Step Programs this year.
            </p>
            <div className="bg-[#F7F4EF] border border-[#DED8EB] rounded p-3 space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={!discForm.has_conflicts} onChange={() => setDiscForm(d => ({ ...d, has_conflicts: false }))} className="mt-1 accent-[#6B3FA0]" data-testid="ob-no-conflicts" />
                <div>
                  <span className="text-sm font-medium text-[#2D1B4E]">I have no conflicts to disclose</span>
                  <p className="text-[11px] text-[#8E85A0]">I am not aware of any financial, business, or personal relationships that could compromise my judgment.</p>
                </div>
              </label>
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="radio" checked={discForm.has_conflicts} onChange={() => setDiscForm(d => ({ ...d, has_conflicts: true }))} className="mt-1 accent-[#6B3FA0]" data-testid="ob-has-conflicts" />
                <div className="flex-1">
                  <span className="text-sm font-medium text-[#2D1B4E]">I have conflicts to disclose</span>
                  <p className="text-[11px] text-[#8E85A0]">Describe each relationship below. The board chair will review.</p>
                </div>
              </label>
              {discForm.has_conflicts && (
                <textarea
                  value={discForm.conflicts_text}
                  onChange={e => setDiscForm(d => ({ ...d, conflicts_text: e.target.value }))}
                  rows={4}
                  placeholder="e.g. My spouse owns 30% of XYZ Vendor LLC, which provides janitorial services…"
                  className="w-full border border-[#DED8EB] rounded p-2 text-sm mt-1"
                  data-testid="ob-conflicts-text"
                />
              )}
            </div>
            <p className="text-[11px] italic text-[#8E85A0]">
              Signed by <strong>{state.linked_member_name || user.name}</strong> on <strong>{new Date().toLocaleDateString()}</strong>.
            </p>
            <div className="flex justify-between pt-3">
              <Button variant="ghost" onClick={skipDisclosure} disabled={busy} className="text-[#8E85A0]" data-testid="ob-skip-disclosure">
                Skip for now
              </Button>
              <Button onClick={signDisclosure} disabled={busy || (discForm.has_conflicts && !discForm.conflicts_text.trim())} className="bg-[#6B3FA0] hover:bg-[#5A2F8F]" data-testid="ob-sign-disclosure">
                {busy ? 'Signing…' : 'Sign & continue'} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* === Step 3: Upcoming meetings === */}
        {step === 3 && (
          <div className="space-y-3 pt-3" data-testid="onboarding-step-meetings">
            <div className="flex items-center gap-2 text-[#2D1B4E]">
              <Calendar className="h-4 w-4 text-[#6B3FA0]" />
              <h3 className="font-bold">Upcoming Meetings & Packets</h3>
            </div>
            <p className="text-xs text-[#5A4D6E]">
              Here's what's on the calendar. Click <strong>Meetings → Packet</strong> on any meeting to open the full packet viewer (with all linked documents) or download it as a ZIP.
            </p>
            {state.upcoming_meetings.length === 0 ? (
              <div className="bg-[#F7F4EF] border border-dashed border-[#DED8EB] rounded p-6 text-center text-sm text-[#8E85A0]">
                No upcoming meetings scheduled yet.
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {state.upcoming_meetings.map(m => (
                  <div key={m.id} className="border border-[#DED8EB] bg-white rounded p-3" data-testid={`ob-meeting-${m.id}`}>
                    <div className="flex items-start justify-between flex-wrap gap-2">
                      <div className="min-w-0">
                        <p className="font-bold text-sm text-[#2D1B4E]">{m.title}</p>
                        <p className="text-[11px] text-[#5A4D6E]">
                          {m.meeting_date}{m.time_start ? ` · ${m.time_start}` : ''}{m.location ? ` · ${m.location}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {m.has_agenda_file && <Badge className="text-[10px] bg-[#EDE7F6] text-[#6B3FA0]"><FileText className="h-2.5 w-2.5 inline mr-0.5" /> Agenda</Badge>}
                        {m.packet_doc_count > 0 && <Badge className="text-[10px] bg-[#F0EDF6] text-[#5A4D6E]">{m.packet_doc_count} doc{m.packet_doc_count === 1 ? '' : 's'}</Badge>}
                      </div>
                    </div>
                    {m.agenda.length > 0 && (
                      <ol className="mt-2 text-[11px] text-[#5A4D6E] pl-4 list-decimal space-y-0.5">
                        {m.agenda.slice(0, 3).map((a, i) => (
                          <li key={i}>{a.title}{a.duration_min ? ` (${a.duration_min}m)` : ''}</li>
                        ))}
                        {m.agenda.length > 3 && <li className="text-[10px] italic text-[#8E85A0]">+ {m.agenda.length - 3} more</li>}
                      </ol>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="flex justify-end pt-3">
              <Button onClick={finishWizard} disabled={busy} className="bg-[#43A047] hover:bg-[#388E3C] text-white" data-testid="ob-finish-btn">
                {busy ? 'Saving…' : 'Finish & open Board Portal'} <CheckCircle2 className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
